import React, { createContext, useCallback } from 'react';
import _get from 'lodash/get';

import { useFetch, useForm, useMutation, useSegment } from 'utils/hooks';
import { createFormData, createPayload } from './utils';
import { FORM_SCHEMA, DEFAULT_FORM_DATA } from './stubs';

/**
 * @type {React.Context<{
 *  data: import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>,
 *  initialData: import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>,
 *  errors: Partial<import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>>,
 *  isDirty: boolean,
 *  isError: boolean,
 *  isLoading: boolean,
 *  isProcessing: boolean,
 *  onSave: () => Promise<any>,
 *  onReload: () => void
 *  onValidate: (updates: Partial<import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>>) => void,
 *  onChange: (updates: Partial<import("zod").z.infer<typeof import("./stubs").FORM_SCHEMA>>) => void,
 * }>}
 */
const ReturnProcessingFeeContext = createContext({});

export const useReturnProcessingFee = () => {
  const context = React.useContext(ReturnProcessingFeeContext);
  if (!context) {
    throw new Error(
      'useReturnProcessingFee must be used within a ReturnProcessingFeeProvider'
    );
  }
  return context;
};

export const ReturnProcessingFeeProvider = ({ children }) => {
  const { track } = useSegment();
  const {
    data,
    initialData,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    dataSchema: FORM_SCHEMA,
    defaultData: DEFAULT_FORM_DATA
  });

  const { isError, isLoading, fetchData: onReload } = useFetch(
    'configure.feesAndIncentives.returnProcessingFees',
    {
      onSuccess: data => onSetInitialData(createFormData(data))
    }
  );

  const { isProcessing, mutate } = useMutation(
    'configure.feesAndIncentives.returnProcessingFees',
    'PUT',
    {
      errorMessage: 'Unable to save Return Processing Fee settings.',
      routeParam: _get(data, 'id'),
      payload: () => createPayload(data),
      onValidate: () => onValidate(data),
      onSuccess: data => {
        track('FTU Wizard Fee Configured', {
          fee_type: 'return_processing_fee',
          is_active: data.is_active,
          return_fee: data.return_fee,
          exchange_fee: data.exchange_fee
        });
        onSetInitialData(createFormData(data));
      }
    }
  );

  const onSave = useCallback(() => {
    return new Promise((resolve, reject) => {
      mutate({}, (err, data) => {
        if (err) reject(err);
        else resolve(data);
      });
    });
  }, [mutate]);

  return (
    <ReturnProcessingFeeContext.Provider
      value={{
        data,
        initialData,
        errors,
        isDirty,
        isError,
        isLoading,
        isProcessing,
        onReload,
        onValidate,
        onChange,
        onSave
      }}
    >
      {children}
    </ReturnProcessingFeeContext.Provider>
  );
};
